// frontend/src/i18n/index.js

import en from './en'
import es from './es'
import { useMemo } from 'react'
import { useLanguage } from './LanguageContext'


/**
 * Diccionarios de idiomas soportados.
 */
const LANGUAGES = { en, es }

// Variable para idioma actual usada en código no React.
let currentLang = 'en'

/**
 * Cambia el idioma actual para código fuera de React.
 * @param {string} lang - Código del idioma ('en', 'es', etc.)
 */
export const setCurrentLanguage = (lang) => {
  if (LANGUAGES[lang]) currentLang = lang
}

/**
 * Traduce una clave a su texto correspondiente fuera de componentes React.
 * Soporta interpolación de variables y pluralización básica.
 * @param {string} key - Clave de texto a traducir
 * @param {object} params - Parámetros para interpolación (ej. {count: 3})
 * @returns {string} Texto traducido con variables reemplazadas
 */
export const translate = (key, params = {}) => {
  const dict = LANGUAGES[currentLang] || {}
  let template = typeof dict[key] === 'string' ? dict[key] : key

  if ('count' in params) {
    const pluralKey = params.count === 1 ? key : key + 's'
    template = typeof dict[pluralKey] === 'string' ? dict[pluralKey] : pluralKey
  }

  return typeof template === 'string'
    ? template.replace(/\{\{(\w+)\}\}/g, (_, k) =>
        params[k] !== undefined ? params[k] : `{{${k}}}`
      )
    : key
}

/**
 * Alias para facilitar la importación y uso con el nombre '_'.
 */
export const _ = translate

/**
 * Hook React para traducción reactiva dentro de componentes.
 * Obtiene el idioma actual desde contexto y traduce claves.
 * @returns {{ _: function }} Función de traducción '_'
 */
export const useTranslation = () => {
  const { language } = useLanguage();

  const _ = useMemo(() => {
    return (key, params = {}) => {
      const dict = LANGUAGES[language] || {};
      let template = typeof dict[key] === 'string' ? dict[key] : key;

      if ('count' in params) {
        const pluralKey = params.count === 1 ? key : key + 's';
        template =
          typeof dict[pluralKey] === 'string' ? dict[pluralKey] : pluralKey;
      }

      return typeof template === 'string'
        ? template.replace(/\{\{(\w+)\}\}/g, (_, k) =>
            params[k] !== undefined ? params[k] : `{{${k}}}`
          )
        : key;
    };
  }, [language]);

  return { _, lang: language };
};
